/**
 * Command Desk — `/` (module 00).
 *
 * Spec: `docs/specs/control-centre/01-command-desk.md`. Mockup:
 * `docs/design/draft-3-editorial-v2.html`, which is this page's own design and which the
 * Technical Delegate has asked the build to match as closely as it can.
 *
 * §1: *"It is the only page the TD should need open before the first push-back."* It answers
 * three questions without a click — is today covered, what must I chase, what is coming — and
 * it **owns no primary data**: every figure on it is composed, in the browser, from routes the
 * other modules already serve. Nothing here writes tournament data. §14 allows this page
 * exactly two writes, and one of them (the mailbox fetch) has nothing to write to in this
 * build, so the alert `Done` state of gap N4 is the only thing it changes.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IT FETCHES, AND WHY IT IS FIVE READS AND NOT ONE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * There is no `GET /command-desk`, and this build deliberately does not add one. Every panel
 * is a view over data another module already publishes — fixtures, officials, report progress,
 * hearings, appointments — and an aggregate route would be a sixth place for the same numbers
 * to be computed, which is a sixth place for them to disagree with the module they came from.
 * The composition is here, in `features/desk/derive.ts`, where it can be tested without a
 * server.
 *
 * Four of those reads are unfiltered and happen once. The fifth — `GET /appointments` — is
 * scoped to **one day**, so the desk asks it for the day on screen and for the days on the
 * current lookahead page, and no more. That is at most four calls and it is bounded by the
 * pager: turning the page fetches the next set rather than the desk holding the whole
 * tournament's appointments in memory to draw a status pill.
 *
 * **RUN 53 (Alfred, 2026-09-03) adds a sixth and a seventh, and they are both schedule-wide.**
 * Superseding the reading above that the desk holds no tournament-wide appointment data: §10's
 * two workload summaries now do. They were built from the per-day views, which made them a
 * four-day slice of a figure every reader takes for the tournament's, and Alfred read `0/1`
 * against a schedule he had drafted in full. So `GET /appointments` with no day and `GET
 * /umpire-appointments` with no scope are fetched once each, on load, and feed §10 and nothing
 * else. Everything the per-day reads drove, they still drive — the status band, the crew
 * sheets, the lookahead, the alerts — and the two sources are never merged.
 *
 * **RUN 82 (the Technical Delegate's Command Desk addendum, approved 2026-09-17) adds no read.**
 * The lookahead page is four days now (§7a), so the per-day read above is at most FIVE calls —
 * the day on screen and four ahead — where it was four. And the schedule-wide technical read
 * gains a second reader: §8b's `Actual Appointments — Completed`, the locked record, is drawn
 * from its fixtures and their slots' `lockedAt`, beside §10 and still merged with nothing. The
 * crew line and chip on `Today's Matches` (§6a) read the day's own appointments, which that
 * panel already held.
 *
 * **RUN 82C (the independent QA of that addendum) adds no read either. It splits one, and asks
 * three again.** The per-day read is two reads now — the focus day's own, and the lookahead
 * page's — so turning the pager re-reads the page and leaves today's crew lines and chips on
 * screen. And the lock state is no longer the lock state at page load: the schedule-wide technical
 * read and both per-day reads are asked again together every five minutes and whenever the tab
 * comes back into view, keeping the last good answer on screen until the new one lands
 * (`useRefreshingAsync`). No timer is added for it — the five minutes are read off the minute the
 * hero's clock already keeps. The fixtures, roster, reports, hearings and umpire reads are still
 * read once. And every figure on the page that asks whether a match has been played now asks the
 * same function, `matchCompleted`, against that same minute.
 *
 * **RUN 82G (the second independent QA) adds no read, and asks one more again.** The umpire read
 * behind §10's Umpires table is asked again on the same key as the technical reads, so the two
 * halves of §10 are never read at different times. The asking itself is quieter: a return to the
 * tab or window within thirty seconds of the last ask asks nothing, a window taking focus counts
 * as a return (two windows side by side never change visibility), and a read still out is not
 * asked again unless it has been out for more than ten minutes (`useRefreshingAsync`). Today's Matches and §8b say when they were read, and say so
 * once that is more than ten minutes ago. The rail's Appointment Status is counted over the days
 * the desk actually holds, so a page turn no longer dips it; no crew alert is raised before the
 * day's crews are read; and the day and the countdown are Tokyo's, not the laptop's.
 *
 * **RUN 82J (the third independent QA, 2026-09-18) adds one read, and only on a later page.** The
 * rail's Appointment Status counts two fixed days now — the focus day and the next match day — so
 * the next match day is a read of its own, asked on the same refresh key, whatever page the
 * lookahead is on. On the first page that day is taken out of the page's read, so the calls there
 * are the five they were; on a later page they are six. Nothing on the desk says anything about a
 * day's crews before that day's read has landed; hearing alerts are measured against the real
 * today, not the focus day; and coming back online counts as a return.
 *
 * **RUN 86 adds one read, and it is the cards'.** `GET /cards` - the TMS public cards reports as
 * the server's poller last read them - feeds the rail's Cards Listing and Cards Average Per Game,
 * and is asked again on the same refresh key as the lock reads. Both panels keep their named empty
 * states until a report has been read, and say why none has. Its own `As Of` joins the page's
 * `Refresh Failing` announcer, and a second hidden `status` says which competitions' latest TMS
 * reads failed - changed only when that set changes, never at a read's tick.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE THE MOCKUP AND THE SPECIFICATION DIVERGE FROM WHAT CAN BE BUILT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The mockup wins on visuals and the specification wins on behaviour; where the design shows
 * a figure this build has no store for, the panel draws a named empty state rather than a
 * plausible number. The three panels in that position — Cards Listing, Cards Average Per Game
 * and the Inbox Monitor — each say which source they want. The stylesheet's own header lists
 * what was not ported and why. (RUN 53: the Umpires half of §10 is no longer among them —
 * module 02 is built, and that panel is drawn from its store.)
 *
 * The masthead, module strip and footer of §3 and §13 are the Shell's and are not redrawn
 * here; §3's `masthead__event` block and §13's mark slots belong to that component and to
 * Configuration §01, which is where the Shell already reads the event's name from.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  fetchAppointments,
  fetchAppointmentsSchedule,
  fetchCards,
  fetchFixtures,
  fetchHearings,
  fetchOfficials,
  fetchReportProgress,
  fetchUmpireAppointments,
} from "../api/client.js";
import type {
  AppointmentsViewBody,
  CardsBody,
  FixtureListBody,
  HearingListBody,
  OfficialListBody,
  ReportProgressBody,
  UmpireAppointmentsViewBody,
} from "../api/types.js";
import { HowToLink, HowToUse } from "../app/HowToUse.js";
import { FailBanner, Skeleton } from "../app/states.js";
import { useAsync, useRefreshingAsync, type RefreshingAsyncState } from "../app/useAsync.js";
import { cardsAnnouncement, servedClock } from "../features/desk/cards.js";
import { useTournament } from "../app/session.js";
import { COMPETITION_TABS, Tabs } from "../features/appointments/Tabs.js";
import type { CompetitionFilter } from "../features/appointments/derive.js";
import { countOf } from "../features/appointments/vocabulary.js";
import { ActualAppointmentsPanel } from "../features/desk/ActualAppointmentsPanel.js";
import {
  TechTableSummary,
  UmpiresSummary,
  type TechRoleFilter,
  type UmpireGenderFilter,
} from "../features/desk/AppointmentSummaries.js";
import {
  AlertsPanel,
  AppointmentStatusPanel,
  CardsAveragePanel,
  CardsListingPanel,
  InboxPanel,
  OfficialsDonutPanel,
  PoolProgressPanel,
  ReportsOutstandingPanel,
  UsefulLinksPanel,
  type CrewAlertsState,
} from "../features/desk/DeskRail.js";
import { useBrandAsset } from "../features/branding/useBrandAsset.js";
import { LookaheadPanel, TodayPanel } from "../features/desk/MatchPanels.js";
import { UmActivityPanel } from "../features/desk/UmActivityPanel.js";
import { CapMilestonesPanel } from "../features/desk/CapMilestonesPanel.js";
import { TdQuickNoteCapture } from "../features/officials/TdQuickNoteCapture.js";
import {
  appointmentStatus,
  appointmentWindow,
  byKickOff,
  countdown,
  dayNote,
  dayStrip,
  deskAlerts,
  focusDay,
  indexAppointments,
  lockedRecord,
  longDate,
  lookaheadDayRead,
  matchDays,
  nextFixture,
  nextMatchDay,
  officialsDonut,
  playedMatchIds,
  poolProgress,
  provisionalCrews,
  readStamp,
  shortDate,
  tallyDay,
  technicalWorkloadRows,
  todayIso,
  umpireWorkloadRows,
  type DayReadState,
} from "../features/desk/derive.js";

/**
 * §7's `Days per page`, which Configuration §07 is supposed to supply as `Fixtures Lookahead
 * — Days Shown Under Tomorrow Onwards`. The configuration store has no such field, so the
 * specification's own default stands and both the panel foot and the Task 3 report say so.
 *
 * **RUN 82 (§7a, amended 2026-09-17): four, not the default three** — the Technical Delegate's
 * Command Desk addendum, which pages Tomorrow Onwards four days at a time as part of balancing
 * the two columns once Cards Listing moved to the rail. Everything that pages — the slice, the
 * page count, the day strip's jump and the day note's `shown on page N` — divides by this one
 * constant, and the panel foot is handed it rather than restating it.
 *
 * **RUN 82C — unchanged, and attributed accurately.** Four is the Run 82 work order's `3→4`; the
 * mockup approved on 17 September still pages by three in its own script. Balancing the columns
 * is the reason the work order gives, not a measurement: nothing has measured the columns at
 * four days a page, in the mockup or in this build.
 */
const LOOKAHEAD_DAYS_PER_PAGE = 4;

/**
 * RUN 82T — a tuple of exactly `N` of `T` (final delta QA, H-1, 2026-09-18).
 *
 * §7's page is {@link LOOKAHEAD_DAYS_PER_PAGE} days and each of them is read on its own now, so the
 * page calls one `useDayAppointments` per day of a page — and React fixes the number of hooks a
 * render may call, which means those calls are written out one by one and cannot be a loop over the
 * days. This type is the guard on that: the reads are collected into a tuple of this length, so
 * raising the page size without writing another read fails the build here rather than leaving the
 * last day of every page at `Crew loading` for ever, which is the same class of silent wrong answer
 * H-1 was.
 */
type Tuple<T, N extends number, R extends T[] = []> = R["length"] extends N
  ? R
  : Tuple<T, N, [...R, T]>;

/** RUN 82T — the lookahead page's reads, one per day of a page. */
type PageDayReads = Tuple<
  RefreshingAsyncState<AppointmentsViewBody | null>,
  typeof LOOKAHEAD_DAYS_PER_PAGE
>;

/**
 * RUN 82C — how often, on the tournament clock, the desk asks the lock state again: every five
 * minutes, on the five-minute marks.
 *
 * **Why five.** The lock is set by one person pressing one button on another page. A desk that
 * never re-read it drew yesterday evening's `Draft` chips beside a day locked this morning, and
 * the tally beside them drifted from the chips as soon as either read was older than the other.
 * Every five minutes is at most five one-day reads and one schedule-wide read of a few tens of
 * kilobytes, and short enough that a lock pressed at the start of a half-time break is on the desk
 * before the break ends. Coming back to the tab asks at once, which covers the case that matters
 * most: the Technical Delegate locks the day in one tab and returns to this one.
 *
 * **Why no timer.** The key is `Math.floor(clockMinute / REFRESH_MINUTES)`, read off the minute
 * the page already keeps for the hero, so a refresh is a re-render the one-second tick was already
 * making, and there is no second interval to leak, to throttle or to forget to clear.
 */
const REFRESH_MINUTES = 5;

/** The one-second tick the hero countdown and §4's clock need, and nothing else uses. */
function useSecond(): Date {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, []);
  return now;
}

/**
 * RUN 82G — how soon after the desk last asked a return to it may ask again: **thirty seconds**.
 *
 * The second independent QA (L3) found returns storming. Flicking between this tab and TO
 * Appointments asked every read again on every flick, and a return that lands a second after the
 * five-minute mark asked for exactly what the mark had just asked for. Inside thirty seconds of the
 * last ask the answer on screen was asked for moments ago, and asking again buys nothing but load
 * on a venue connection; past them, a return asks at once, as it always did.
 */
const RETURN_GAP_MS = 30_000;

/**
 * RUN 82C — how many times this tab has come back into view: the other half of the refresh key.
 *
 * A listener rather than a timer, and a count rather than a flag, so that every return is a new
 * key and asks again. Going OUT of view asks nothing by itself. It does not stop the five-minute
 * mark either: a hidden tab's clock still ticks, as slowly as the browser throttles it, and each
 * mark it reaches still asks — so a return asks at once, whatever the hidden tab last read.
 *
 * **RUN 82G — a window taking focus is a return too, and neither counts inside {@link
 * RETURN_GAP_MS} of the last ask (second QA, L3).** Two browser windows side by side never change
 * visibility: the Technical Delegate locks the day in one and clicks back into the other, and
 * `visibilitychange` never fires. `focus` on the window does. Switching tabs fires both, one after
 * the other — which is one reason the gap exists. `lastAsked` is when the desk last asked, and the
 * page moves it on whenever the refresh key changes, a return's own included. (If the second of
 * the two events arrives before that, and counts again, the key still moves once per render and
 * the read that is out is not asked twice — `useRefreshingAsync` skips it.)
 *
 * **RUN 82J — the network coming back is a return too, on the same terms (third QA, L7).** A laptop
 * woken from sleep brings the tab into view before its Wi-Fi has rejoined: that return asks, the
 * reads fail, and until RUN 82J nothing asked again before the next five-minute mark — so `Refresh
 * Failing` could stand for up to five minutes over a connection that had come back. `online` fires
 * when it does. It passes the same thirty-second gate, and asks nothing for a tab out of view,
 * whose own return will ask.
 */
function useReturns(lastAsked: React.RefObject<number>): number {
  const [returns, setReturns] = useState(0);
  useEffect(() => {
    const onReturn = (): void => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastAsked.current < RETURN_GAP_MS) return;
      setReturns((count) => count + 1);
    };
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    window.addEventListener("online", onReturn);
    return () => {
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
      window.removeEventListener("online", onReturn);
    };
  }, [lastAsked]);
  return returns;
}

/**
 * RUN 82J — what a screen reader is told when a stamped read starts, or stops, failing to refresh
 * (third QA, L9).
 *
 * `Refresh Failing` joins an `As Of` stamp quietly, by design (RUN 82G): no alert, nothing that
 * interrupts. But nothing announced it at all (WCAG 4.1.3), so a screen-reader user learned that the
 * figures had stopped moving only by reading every stamp again. The stamps themselves cannot become
 * live regions: their time changes whenever a refresh lands, which is every five minutes all day. So
 * the page keeps one visually hidden `status` whose words change only when the set of failing stamps
 * does — into failing, naming each panel and when it was last read; out of it, saying so — and say
 * nothing new however often the stamps are redrawn in between.
 *
 * `failing` is that set as one string, so the effect runs when the set changes and at no other time.
 * A failing stamp's time cannot move while it fails: it moves only when an answer lands, and an
 * answer that lands is not old.
 */
function useRefreshAnnouncement(failing: string): string {
  const [words, setWords] = useState("");
  useEffect(() => {
    setWords((current) =>
      failing !== ""
        ? `Refresh Failing — ${failing}.`
        : current === ""
          ? ""
          : "Refresh Working Again — no As Of stamp on the desk is more than ten minutes old.",
    );
  }, [failing]);
  return words;
}

/**
 * Follow-up round, item 2b - the device's MONOTONIC time (`performance.now()`) at which `answer`
 * first reached this page, kept per answer: a new answer object is a new arrival, a re-render of
 * the same one is not. Monotonic, because it is subtracted from itself later, and the device's
 * wall clock can be set, or be wrong, while the page is open.
 */
function useArrival(answer: unknown): number | null {
  const seen = useRef<{ answer: unknown; at: number } | null>(null);
  if (answer === null) return null;
  if (seen.current === null || seen.current.answer !== answer) {
    seen.current = { answer, at: performance.now() };
  }
  return seen.current.at;
}

/**
 * RUN 86 (QA M2) - what a screen reader is told about the TMS cards reads: which competitions'
 * latest reads failed, and how (`cardsAnnouncement`), in one visually hidden `status` - and, once
 * none is failing any more, that they are being read again. The failure lines on screen carry the
 * time of the latest failed read, which moves on every five-minute read while TMS is down, so they
 * are not live; these words have no time in them, and change only when the set of failures does.
 */
function useCardsAnnouncement(failing: string): string {
  const [words, setWords] = useState("");
  useEffect(() => {
    setWords((current) =>
      failing !== ""
        ? failing
        : current === ""
          ? ""
          : "The TMS cards reports are being read again.",
    );
  }, [failing]);
  return words;
}

/**
 * RUN 82J — where one day's own appointments read stands (third QA, M3): `read` once an answer for
 * THAT day is held, `failed` once the read has been refused without ever answering, `loading`
 * otherwise. No day at all — no focus day, or no match day after it — has nothing to read, and
 * counts as read.
 *
 * Read off the body held, as `bulkDaysKey` is, and not off a `ready` beside the day: for one render
 * after the day changes, that `ready` is the previous day's (RUN 82G, L11 — where this was written
 * inline for the focus day alone).
 */
function dayRead(
  day: string | null,
  read: RefreshingAsyncState<AppointmentsViewBody | null>,
): CrewAlertsState {
  if (day === null) return "read";
  if (read.status === "ready" && read.data?.scope.day === day) return "read";
  return read.status === "error" ? "failed" : "loading";
}

/**
 * RUN 82T — one day's own `GET /appointments?day=`, on the desk's refresh key (final delta QA, H-1,
 * 2026-09-18).
 *
 * The focus day's read and the next match day's were written out twice, identically, by RUN 82C and
 * RUN 82J. RUN 82T needs the same read once per day of the lookahead page as well — six copies of
 * one closure — so it is lifted here, unchanged: `null` for no day, which resolves to `null` without
 * asking the server, and the day itself as the read's one dependency, so that a change of day is a
 * new question and a refresh key is not.
 *
 * Every rule {@link useRefreshingAsync} states then holds PER DAY, which is the whole of H-1's fix:
 * a refusal of one day is that day's, the other days keep their answers, a read of a day still out
 * is not asked again until it is stranded, answers apply by number, and an answer already on screen
 * survives a refresh that fails.
 */
function useDayAppointments(
  day: string | null,
  refreshKey: string,
): RefreshingAsyncState<AppointmentsViewBody | null> {
  return useRefreshingAsync<AppointmentsViewBody | null>(
    () => (day === null ? Promise.resolve(null) : fetchAppointments({ day })),
    [day],
    refreshKey,
  );
}

export function CommandDeskPage(): React.JSX.Element {
  const tournament = useTournament();
  const now = useSecond();
  const today = todayIso(now);
  /**
   * The clock, read to the minute — RUN 82's reading for §8b, lifted here in RUN 82C because
   * every figure on the page that asks whether a match has been played now reads it.
   *
   * Whether a match has been played is partly a question of the time — kick-off plus
   * `MATCH_LENGTH_MINUTES`, or a `live` status's hold — so those figures have to move as the
   * clock does, and `now` ticks every second for the hero. Keying them on the minute recomputes
   * them sixty times less often and loses nothing: kick-offs are whole minutes, so the answer can
   * only change on one. `minute` is one `Date` per minute, so the memos below see a change only
   * when there is one.
   */
  const clockMinute = Math.floor(now.getTime() / 60_000);
  const minute = useMemo(() => new Date(clockMinute * 60_000), [clockMinute]);
  /**
   * RUN 82G — when the desk last asked its refreshed reads, on the device's own clock: the moment
   * the refresh key last changed — the load, a five-minute mark or a return that counted. A ref,
   * because the return listener reads it between renders and it must not cause one.
   */
  const lastAsked = useRef(0);
  const tabReturns = useReturns(lastAsked);
  /** RUN 82C — the same key for every read that is asked again, so they are asked together. */
  const refreshKey = `${String(Math.floor(clockMinute / REFRESH_MINUTES))}:${String(tabReturns)}`;
  useEffect(() => {
    lastAsked.current = Date.now();
  }, [refreshKey]);
  /*
   * §4's hero image. Not one of the four reads below and deliberately not through `useAsync`: it
   * is an image behind a band that draws perfectly well without it, so it has no loading state
   * on this page, no failure banner and no place in the page's own status. A slot with nothing
   * in it and a fetch that failed are the same answer here — the gradient — which is what
   * `useBrandAsset` resolves both to.
   */
  const hero = useBrandAsset("hero");

  /* ── The four unfiltered reads ─────────────────────────────────────────── */

  const schedule = useAsync<FixtureListBody>(() => fetchFixtures(), []);
  const roster = useAsync<OfficialListBody>(() => fetchOfficials(), []);
  const reports = useAsync<ReportProgressBody>(() => fetchReportProgress(), []);
  const hearings = useAsync<HearingListBody>(() => fetchHearings({}), []);

  const fixtures = schedule.status === "ready" ? schedule.data.fixtures : [];
  const officials = roster.status === "ready" ? roster.data.officials : [];
  /*
   * Hearings feed two things and are load-bearing for neither: the day strip's orange DC dot
   * and the top alert. `GET /hearings` is `requireTd`, so a Umpires Manager who followed a
   * link to this path gets a 401 here — and the desk goes on drawing, with no hearing dot and
   * no hearing alert, rather than turning one refused panel into a page of red banners. The
   * same is true of the appointment reads below.
   */
  const cases = hearings.status === "ready" ? hearings.data.hearings : [];

  /* ── Which day, and which days ahead ───────────────────────────────────── */

  const focus = useMemo(() => focusDay(fixtures, today), [fixtures, today]);
  /**
   * RUN 82J — the match day after the focus day, in the whole schedule: the second day of §9's
   * window (third QA, L5). Not the lookahead's first day, which the competition filter can move.
   */
  const nextDay = useMemo(() => nextMatchDay(fixtures, focus.day), [fixtures, focus.day]);
  const [competition, setCompetition] = useState<CompetitionFilter>("all");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<number | null>(null);
  /** RUN 31 — the lookahead panel's own open sheet, independent of today's. */
  const [aheadExpanded, setAheadExpanded] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [actioned, setActioned] = useState<ReadonlySet<string>>(() => new Set());
  const [techRole, setTechRole] = useState<TechRoleFilter>("all");
  const [techPage, setTechPage] = useState(1);
  const [umpGender, setUmpGender] = useState<UmpireGenderFilter>("all");
  const [umpPage, setUmpPage] = useState(1);

  const todayFixtures = useMemo(
    () =>
      [...fixtures].filter((fixture) => fixture.date === focus.day).sort(byKickOff),
    [fixtures, focus.day],
  );

  /** Every day after the focus day that still holds a fixture under the competition filter. */
  const aheadDays = useMemo(() => {
    const inFilter = fixtures.filter(
      (fixture) =>
        (competition === "all" || fixture.competitionGender === competition) &&
        focus.day !== null &&
        fixture.date > focus.day,
    );
    return matchDays(inFilter);
  }, [fixtures, competition, focus.day]);

  const pageCount = Math.max(1, Math.ceil(aheadDays.length / LOOKAHEAD_DAYS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageDays = aheadDays.slice(
    (currentPage - 1) * LOOKAHEAD_DAYS_PER_PAGE,
    currentPage * LOOKAHEAD_DAYS_PER_PAGE,
  );

  const lookahead = useMemo(
    () =>
      pageDays.map((date) => ({
        date,
        fixtures: fixtures
          .filter(
            (fixture) =>
              fixture.date === date &&
              (competition === "all" || fixture.competitionGender === competition),
          )
          .sort(byKickOff),
      })),
    [pageDays, fixtures, competition],
  );

  /* ── The fifth read: appointments, for exactly the days on screen ──────── */

  /*
   * RUN 82G — `apptDays`, the focus day and the page's days in one sorted list, stood here. After
   * RUN 82C split the read it had one reader left, §9's window, and the second QA (L4) moved that
   * window onto the days actually HELD (`windowDays`, below), so it had none and was removed.
   *
   * RUN 82J — and `windowDays` has gone the same way: §9's window is two fixed days now
   * (`apptWindow`, below; third QA, L5).
   */

  /**
   * RUN 82C — that one read is two now: the focus day's own, and the lookahead page's.
   *
   * **Why.** It was one `Promise.all` over `apptDays`, so turning the pager changed its key, and
   * the loading state blanked EVERYTHING it fed while the next page arrived — today's crew lines
   * and TMS chips included, on a panel the pager has nothing to do with. Split, the page's days
   * are a new question on every turn and today's is not asked again: its answer stays on screen.
   * `appointed` merges the two, so nothing that reads it had to change.
   *
   * **Both are asked again on the refresh key**, with the schedule-wide read below, so the chips
   * and the locked record move together rather than drifting apart for the length of a page
   * load. A refresh keeps the last good answer on screen; only a new question resets.
   *
   * **RUN 82J — and the next match day is a third (third QA, L5).** Appointment Status counts the
   * focus day and the next match day, whatever page the lookahead is on, and a page's read is a new
   * question on every turn — so a figure fed from it went on following the pager. The next match day
   * is asked on its own now, on the same refresh key, and is taken out of the page's read wherever
   * the page holds it: on the first page the desk asks for exactly the five days it asked for
   * before, and only on a later page is that one day a sixth call. Its body is merged with the
   * others, so its lookahead rows and sheets read it as they read the page's.
   *
   * **RUN 82T — and so is every day of the lookahead page (final delta QA, H-1, 2026-09-18).** The
   * page's days were read by ONE `Promise.all`, and a `Promise.all` rejects on its first refusal: one
   * day the server would not serve lost all four, and `pageRead`'s error was passed to nothing, so
   * `Tomorrow Onwards` drew four days of read-nothing as four days of appointed-nobody —
   * `Appointed` rows flipped to `Scheduled` and their crew boxes read `Unfilled`. Each day of the
   * page is a read of its own now, through the same {@link useDayAppointments} the two days above
   * use, so a refusal is one day's and the other three read exactly as they did. The calls are the
   * same calls in the same number: five on the first page, six on a later one.
   */
  const todayRead = useDayAppointments(focus.day, refreshKey);
  const nextRead = useDayAppointments(nextDay, refreshKey);
  const pageReadDays = pageDays.filter((day) => day !== nextDay);
  /*
   * RUN 82T — one read per day of the page, written out because React fixes the number of hooks a
   * render may call ({@link PageDayReads} is the compile-time guard that this is still the page's
   * own size). A slot with no day resolves to `null` and asks the server for nothing, which is what
   * the last slots are on a short page, on the last page, and under a competition filter.
   */
  const aheadRead0 = useDayAppointments(pageReadDays[0] ?? null, refreshKey);
  const aheadRead1 = useDayAppointments(pageReadDays[1] ?? null, refreshKey);
  const aheadRead2 = useDayAppointments(pageReadDays[2] ?? null, refreshKey);
  const aheadRead3 = useDayAppointments(pageReadDays[3] ?? null, refreshKey);
  const pageReads: PageDayReads = useMemo(
    () => [aheadRead0, aheadRead1, aheadRead2, aheadRead3],
    [aheadRead0, aheadRead1, aheadRead2, aheadRead3],
  );

  const apptViews = useMemo(
    (): AppointmentsViewBody[] =>
      [todayRead, nextRead, ...pageReads].flatMap((read) =>
        read.status === "ready" && read.data !== null ? [read.data] : [],
      ),
    [todayRead, nextRead, pageReads],
  );
  const appointed = useMemo(() => indexAppointments(apptViews), [apptViews]);

  /* ── RUN 31: the lookahead's crew sheets, one day at a time, on demand ──── */

  /**
   * `Tomorrow Onwards` rows now open the same seven-slot sheet `Today's Matches` opens, and a
   * sheet needs that day's appointments. This is the cache they come from.
   *
   * **Lazily, per day, and never twice.** The lookahead can run the length of the tournament;
   * prefetching every remaining day would be a dozen calls on a venue connection to fill boxes
   * the Technical Delegate may never open. So nothing is read until he presses `Sheet` on a
   * row, and then exactly one `GET /appointments?day=` is made for that row's day — which
   * serves every other fixture on it too, so the second and third sheet on the same day cost
   * nothing.
   *
   * `askedFor` is a ref rather than state on purpose: it must be written the instant the
   * request goes out, so that two presses in the same tick cannot both get past the guard, and
   * it must not itself cause a render.
   *
   * **A failure is forgotten deliberately.** `GET /appointments` is `requireTd`; a Umpires
   * Manager who reached this path gets a 401 and the sheet shows its pending state, which is
   * the honest reading — nothing here knows of an appointment. Dropping the day from
   * `askedFor` means a second press retries rather than the desk deciding, once and for ever,
   * that the day is unknowable. It cannot storm: only a deliberate press asks again.
   *
   * **RUN 82T — and a sheet that is reading says so (final delta QA, H-1, 2026-09-18).** *"The sheet
   * shows its pending state, which is the honest reading"* was the same mistake H-1 is: the pending
   * state was seven boxes, three of them reading `Unfilled`, which is a statement about appointments
   * nobody had read. A sheet on a day the page could not read now says `Loading` while its own read
   * is out and `Not loaded` once that read has been refused — RUN 82J's words, from the same
   * `crews` prop — and `lazyPending` is which days are in the first of those two states.
   */
  const [lazyDays, setLazyDays] = useState<ReadonlyMap<string, AppointmentsViewBody>>(
    () => new Map(),
  );
  const askedFor = useRef<Set<string>>(new Set());
  /**
   * RUN 82T — the days a sheet press is reading at this moment (H-1).
   *
   * State and not a second ref, because it is drawn: a read that is out has to reach the sheet as
   * `Loading`, and a read that is refused has to take it back to `Not loaded`, and a ref changing
   * does neither. `askedFor` stays the guard — it must be written in the same tick as the request,
   * so that two presses cannot both get past it — and this is the same set for drawing. A refused
   * day leaves this set and falls back to what its own page read says, which is `failed`.
   */
  const [lazyPending, setLazyPending] = useState<ReadonlySet<string>>(() => new Set());
  const clearPending = useCallback((day: string): void => {
    setLazyPending((current) => {
      const next = new Set(current);
      next.delete(day);
      return next;
    });
  }, []);

  /**
   * The days the bulk read above already holds. A sheet on one of those needs no request at
   * all — the record is in `appointed` — and asking again would be a second call for a body
   * this page is already holding.
   *
   * **RUN 82C — read off the bodies held, never off `ready` beside the days being asked about.**
   * Every day body names its own day (`scope.day`, the route's echo of `?day=`), so the key is
   * exactly the days the desk has an answer for. It used to be `status === "ready"` paired with
   * the days in view, and for one render those are not the same thing: when the days in view
   * change — the schedule arrives, the pager turns — the render that shows the new days still
   * carries the OLD question's `ready` until the read's effect resets it. A press landing in that
   * render was told the desk held days it had never read, skipped its own read, and left the
   * sheet empty for good, since nothing asks twice. The gate caught exactly that on a loaded
   * machine, at the 5a617d6 baseline and after: `held=2026-09-23,2026-09-24,2026-09-25` against
   * two reads that had both been refused.
   */
  const bulkDaysKey = apptViews
    .map((view) => view.scope.day)
    .filter((day): day is string => day !== null)
    .join(",");

  const ensureDayAppointments = useCallback(
    (day: string): void => {
      if (bulkDaysKey.split(",").includes(day)) return;
      if (askedFor.current.has(day)) return;
      askedFor.current.add(day);
      // RUN 82T (H-1) — the sheet says `Loading` from here until this read lands or is refused.
      setLazyPending((current) => new Set(current).add(day));
      void fetchAppointments({ day }).then(
        (view) => {
          setLazyDays((current) => {
            const next = new Map(current);
            next.set(day, view);
            return next;
          });
          clearPending(day);
        },
        () => {
          askedFor.current.delete(day);
          clearPending(day);
        },
      );
    },
    [bulkDaysKey, clearPending],
  );

  /**
   * What the lookahead's rows read: the bulk read's days and whatever a press has fetched, in
   * one map, so a fixture's status pill and its crew sheet can never disagree about which record
   * belongs to it. (Until RUN 82C the pressed days were laid over the bulk read's.)
   *
   * **RUN 82C — the bulk reads now win where both hold a day.** A press only fetches a day the
   * bulk read did not hold at the time, and before RUN 82C nothing was ever read again, so the
   * order did not matter. Now the bulk reads are asked again every five minutes, and a day a
   * press fetched once is not: laid over them, that one old answer would have hidden every newer
   * one for the rest of the session. So the pressed days go in first and the bulk reads over
   * them — a pressed day shows only where no bulk read holds it.
   */
  const lookaheadAppointed = useMemo(
    () => indexAppointments([...lazyDays.values(), ...apptViews]),
    [apptViews, lazyDays],
  );

  /**
   * RUN 82T — where each day the lookahead draws stands, by day (final delta QA, H-1, 2026-09-18).
   *
   * The index above cannot answer this, and that is exactly what H-1 was: a day nobody has read and
   * a day nobody has appointed both index to nothing, and `Tomorrow Onwards` drew the first as the
   * second. So the panel is handed this beside the index, as `Today's Matches` has been handed
   * `crewAlerts` since RUN 82J, and says nothing about a crew until its day is `read`.
   *
   * Three sources, in {@link lookaheadDayRead}'s order. **Held** is the days the desk has a body for
   * — `bulkDaysKey`, which is read off the bodies themselves and not off a `ready` beside a day
   * (RUN 82C's race), plus the days a sheet press fetched. **Pending** is the presses still out.
   * Otherwise the day's own read says: the next match day's, or the page slot that asked for it.
   */
  const lookaheadReads = useMemo((): ReadonlyMap<string, DayReadState> => {
    const own = new Map<string, DayReadState>();
    if (nextDay !== null) own.set(nextDay, dayRead(nextDay, nextRead));
    for (const [index, day] of pageReadDays.entries()) {
      const read = pageReads[index];
      if (read !== undefined) own.set(day, dayRead(day, read));
    }
    const held = new Set(bulkDaysKey.split(",").filter((day) => day !== ""));
    for (const day of lazyDays.keys()) held.add(day);
    return new Map(
      pageDays.map((day) => [day, lookaheadDayRead(day, held, lazyPending, own)] as const),
    );
    // `pageDays` and `pageReadDays` are new arrays on every render; their joined days are what
    // changes, as `bulkDaysKey` is, and that is what this watches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageDays.join(","),
    pageReadDays.join(","),
    pageReads,
    nextDay,
    nextRead,
    bulkDaysKey,
    lazyDays,
    lazyPending,
  ]);

  /* ── RUN 53: §10's two summaries, schedule-wide ─────────────────────────── */

  /**
   * The two reads that feed §10, and nothing else on this page.
   *
   * **Why they exist.** Alfred, 2026-09-03: he had drafted appointments across the whole
   * schedule and the summary tables still read `0/1` and `0/2`, because they were built from
   * the per-day views above — the focus day plus the lookahead page — and captioned themselves
   * `N Match Days In View`. That caption was accurate and useless: the panel a TD reads as *the
   * tournament's* workload must be the tournament's workload. So §10 now has its own source.
   *
   * **One GET each, on load, and no polling.** Both routes have always taken their scope
   * parameters as optional and answered the wider question without them: `GET /appointments`
   * with no day returns every fixture in the schedule with its technical slots and `workloads`
   * measured over ALL stored rows (drafts included), and `GET /umpire-appointments` does the
   * same for both umpire halves. The schedule is ~40 matches × 7 technical slots and ~40 × 4
   * umpire slots per half — a few tens of kilobytes, fetched once. The empty dependency array
   * is the whole of the performance story: these do not re-fetch when the day, the competition
   * filter or the lookahead page changes.
   *
   * **They do not touch anything else.** The per-day reads above still drive the status band,
   * the crew sheets, the lookahead sheets and the alerts, and are unchanged. Nothing merges
   * these two views into `appointed` — a schedule-wide body and a day body would be two
   * answers to the same question, and the day is the authority on the day (§A1).
   *
   * **Their warnings are deliberately dropped.** The schedule-wide technical read is the one
   * scope that carries §A's pair-concentration warnings, because concentration is a
   * tournament-level property. They belong to the Appointments page, which renders them; this
   * page reads `workloads` and `fixtures` off these bodies and nothing else, so no warning
   * from them reaches the desk and none can be shown twice.
   *
   * **A refusal is panel-scoped.** Both are `requireTd`. A Umpires Manager who reached the
   * desk gets a 401 here and sees a banner inside the summary, exactly as the hearings read
   * already fails without taking the page with it.
   *
   * **RUN 82C — superseding `on load, and no polling` for the technical read.** Since RUN 82 it
   * also carries the lock (§8b), and a lock is pressed on another page while this one stays open.
   * So it is asked again on the refresh key, together with the two per-day reads, and keeps its
   * last good answer on screen while it is asked. It is still ONE read, still re-asked by nothing
   * that changes on this page — not the day, the filter or the pager. The umpire read beside it
   * carries no lock, and is still read once.
   *
   * **RUN 82G — superseding `still read once` for the umpire read (second QA, L2).** §10's two
   * tables sit side by side and the technical one moved every five minutes while the umpire one
   * stood at page load, so an Umpires Manager's change made after the desk was opened never
   * reached it, and nothing on the panel said so. It is asked again on the same key now, keeping
   * its last good answer on screen, so the two halves of §10 are always the same age.
   */
  const scheduleAppointments = useRefreshingAsync<AppointmentsViewBody>(
    () => fetchAppointmentsSchedule(),
    [],
    refreshKey,
  );
  const umpireSchedule = useRefreshingAsync<UmpireAppointmentsViewBody>(
    () => fetchUmpireAppointments({}),
    [],
    refreshKey,
  );

  /**
   * RUN 86 - the TMS cards reports, as the server last read them: Sec. 8's Cards Listing and
   * Sec. 9's Cards Average Per Game both draw this one read. It is asked again on the same key as
   * the lock reads - every five minutes and on a return - because the server's poller reads TMS
   * every five minutes, and it keeps its last answer on screen while it is asked. Nothing here
   * reaches TMS: `GET /cards` answers from what the server stored.
   */
  const cards = useRefreshingAsync<CardsBody>(() => fetchCards(), [], refreshKey);
  /**
   * Follow-up round, item 2b - the cards panels' clock is the SERVER's: its `servedAt`, advanced by
   * the device's monotonic time since that answer arrived (`servedClock`). A report's `fetchedAt` is
   * on the server's clock, so its age - the amber `Overdue` stamp - and the day its `As Of` falls on
   * are then one clock compared with itself, whatever the device's wall clock says: a laptop a few
   * seconds fast, or hours out, changes neither. Looked at again each minute (the page's `minute`
   * says when, never what) and with each answer, so the memoised panels render no more often.
   */
  const cardsAnswer = cards.status === "ready" ? cards.data : null;
  const cardsArrival = useArrival(cardsAnswer);
  const cardsNow = useMemo(
    () =>
      cardsAnswer !== null && cardsArrival !== null
        ? servedClock(cardsAnswer.servedAt, performance.now() - cardsArrival)
        : minute,
    [minute, cardsAnswer, cardsArrival],
  );

  const workloads = useMemo(
    () => (scheduleAppointments.status === "ready" ? scheduleAppointments.data.workloads : []),
    [scheduleAppointments],
  );
  const apptFixtures = useMemo(
    () => (scheduleAppointments.status === "ready" ? scheduleAppointments.data.fixtures : []),
    [scheduleAppointments],
  );
  const umpireWorkloads = useMemo(
    () => (umpireSchedule.status === "ready" ? umpireSchedule.data.tmsActual.workloads : []),
    [umpireSchedule],
  );
  const umpireFixtures = useMemo(
    () => (umpireSchedule.status === "ready" ? umpireSchedule.data.fixtures : []),
    [umpireSchedule],
  );

  /* ── The derived panels ────────────────────────────────────────────────── */

  const days = matchDays(fixtures);
  const openingDay = days[0] ?? null;
  const closingDay = days[days.length - 1] ?? null;
  // RUN 82C — every `played` below is `matchCompleted` against the same minute.
  const tally = tallyDay(todayFixtures, minute);

  /**
   * RUN 82G — whether the focus day's own appointments are held (second QA, L11), and so whether a
   * crew alert can be derived at all. Read off the body held, as `bulkDaysKey` is, and not off a
   * `ready` beside the focus day: for one render after the day changes, that `ready` is the
   * previous day's. A day with no fixtures has no crews to read, and counts as read.
   *
   * RUN 82J — through `dayRead`, which the next match day's read shares; and it is no longer the
   * alerts' alone (third QA, M3): Today's Matches and Appointment Status read it too, and say
   * nothing about a crew on the focus day until it is `read`.
   */
  const crewAlerts: CrewAlertsState = dayRead(focus.day, todayRead);
  /** RUN 82J — the same, for the next match day's own read (L5, M3). */
  const nextCrews: CrewAlertsState = dayRead(nextDay, nextRead);

  /**
   * §9's window: the focus day and the day after it — the 48 hours the panel names.
   *
   * **RUN 82G — the days actually held, and the focus day (second QA, L4).** The window was the
   * days in VIEW, and a page turn puts new days in view before their appointments arrive: for the
   * length of that read every match on them counted as not appointed, and the figure dipped on
   * every turn and came back. The window is now the days whose appointments the desk holds, plus
   * the focus day, which is in it from the start and is never merely a page's to lose. The caption
   * counts the same days.
   *
   * **RUN 82J — superseded (third QA, L5 and M3).** What the desk holds still followed the pager, so
   * the figure did too, and the focus day was in the window "from the start" — counted incomplete
   * before its crews were read, and for as long as a refused read stayed refused. The window is two
   * fixed days now, the focus day and the next match day, each counted once its own read has landed
   * (`appointmentWindow`); the caption names the days counted, and a refused day is said.
   */
  const apptWindow = useMemo(
    () =>
      appointmentWindow([
        { day: focus.day, read: crewAlerts },
        { day: nextDay, read: nextCrews },
      ]),
    [focus.day, crewAlerts, nextDay, nextCrews],
  );
  const windowFixtures = useMemo(
    () => fixtures.filter((fixture) => apptWindow.counted.includes(fixture.date)),
    [fixtures, apptWindow],
  );

  const status = appointmentStatus(windowFixtures, appointed);
  const provisional = provisionalCrews(windowFixtures, appointed);
  const donut = officialsDonut(officials);
  const progress = useMemo(() => poolProgress(fixtures, minute), [fixtures, minute]);
  const outstanding =
    reports.status === "ready"
      ? reports.data.denominator - reports.data.counts.drafted
      : 0;
  const reportTotal = reports.status === "ready" ? reports.data.denominator : 0;

  const alerts = useMemo(
    () =>
      deskAlerts({
        // RUN 82J (third QA, H1) — the real today, never the focus day: on a day with no fixtures
        // the focus day is the next match day, and a hearing that day was dropped as past while
        // tomorrow's was called `today`. The crews still come from the focus day's fixtures.
        today,
        focusFixtures: todayFixtures,
        appointed,
        crewsRead: crewAlerts === "read",
        hearings: cases,
        reportsOutstanding: outstanding,
        reportsTotal: reportTotal,
        now: minute,
      }),
    [today, todayFixtures, appointed, crewAlerts, cases, outstanding, reportTotal, minute],
  );

  const strip = useMemo(() => dayStrip(fixtures, cases, today), [fixtures, cases, today]);
  const upcoming = useMemo(() => nextFixture(fixtures, today, minute), [fixtures, today, minute]);
  /**
   * §10's two row sets.
   *
   * The `completed` half of every cell is still counted from played matches and nowhere else:
   * `playedMatchIds` is taken from the schedule read, and the appointment fixtures it is
   * matched against are now the schedule-wide ones, so the pairing is over the same set on
   * both sides. The `appointed` half comes from the route's own measured `workloads`.
   *
   * RUN 82C: `played` is the desk's one rule at this minute — the same matches §8b counts and
   * the dateline calls `Played` — and it is taken once for both tables.
   */
  const played = useMemo(() => playedMatchIds(fixtures, minute), [fixtures, minute]);
  const workloadRows = useMemo(
    () => technicalWorkloadRows(workloads, officials, apptFixtures, played),
    [workloads, officials, apptFixtures, played],
  );
  const umpireRows = useMemo(
    () => umpireWorkloadRows(umpireWorkloads, officials, umpireFixtures, played),
    [umpireWorkloads, officials, umpireFixtures, played],
  );

  /**
   * RUN 82 — §8b's locked record, from the same schedule-wide technical read as §10, against
   * the page's minute (see `clockMinute` above, where RUN 82C moved the reasoning).
   */
  const record = useMemo(
    () => lockedRecord(apptFixtures, fixtures, minute),
    [apptFixtures, fixtures, minute],
  );
  /**
   * RUN 82C — `As Of HH:MM` for §8b's strip: when the read the record is drawn from answered.
   *
   * RUN 82G — and whether that is more than ten minutes ago, read against the one-second `now`
   * rather than the minute, so the stamp turns the moment the answer is ten minutes old (L2).
   */
  const recordAsOf =
    scheduleAppointments.status === "ready" ? readStamp(scheduleAppointments.readAt, now) : null;
  /**
   * RUN 82G — the same for Today's Matches, from the day's OWN read: the crew lines and chips are
   * drawn from it and it answers on its own schedule (L2). No stamp for a day with no fixtures —
   * nothing was asked of the server for it.
   */
  const todayAsOf =
    todayRead.status === "ready" && todayRead.data !== null && todayRead.data.scope.day === focus.day
      ? readStamp(todayRead.readAt, now)
      : null;
  /**
   * RUN 82J — the stamps that say `Refresh Failing`, as one string, for the hidden `status` that
   * announces a change into or out of failing and nothing else (third QA, L9).
   */
  /**
   * RUN 86 (QA M3) - the same for the cards read, which both cards panels draw: its stamp says
   * `Refresh Failing` under the report's own As Of, and joins the announcer below. The panels are
   * handed only the label, and only while it fails, so that they render when that changes and not
   * on every second of the clock.
   */
  const cardsAsOf = cards.status === "ready" ? readStamp(cards.readAt, now) : null;
  const cardsStale = cardsAsOf?.failing === true ? cardsAsOf.label : null;
  const refreshAnnouncement = useRefreshAnnouncement(
    [
      todayAsOf?.failing === true ? `Today's Matches ${todayAsOf.label}` : null,
      recordAsOf?.failing === true ? `Actual Appointments ${recordAsOf.label}` : null,
      // Fix round, item 11: in the words the two cards panels print - `Desk Read 10:59` - so what
      // is heard is what is on screen, and never read as the TMS report's own As Of.
      cardsStale === null ? null : `Cards Desk Read ${cardsStale.replace(/^As Of /, "")}`,
    ]
      .filter((entry): entry is string => entry !== null)
      .join(", "),
  );
  /** RUN 86 (QA M2) - which competitions' latest TMS cards reads failed, for the cards announcer. */
  const cardsAnnounced = useCardsAnnouncement(
    cards.status === "ready" ? cardsAnnouncement(cards.data) : "",
  );

  const onAction = useCallback((id: string, done: boolean) => {
    setActioned((current) => {
      const next = new Set(current);
      if (done) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const onToggle = useCallback((tmsMatchDbId: number) => {
    setExpanded((current) => (current === tmsMatchDbId ? null : tmsMatchDbId));
  }, []);

  /**
   * RUN 31 — the same toggle for `Tomorrow Onwards`, with the day's read attached to the
   * OPENING of a sheet and not to its closing: shutting a sheet is not a reason to fetch
   * anything, and `ensureDayAppointments` is idempotent anyway.
   *
   * The two panels keep separate `expanded` state so that opening a fixture on Thursday does
   * not shut the one the TD has open on today's matches above it — they are two questions and
   * he may well be comparing the answers.
   *
   * The fetch is fired from the handler rather than from inside the state updater: an updater
   * may be run more than once for one press, and a request is not something to run twice.
   */
  const onToggleAhead = useCallback(
    (tmsMatchDbId: number, day: string) => {
      const opening = aheadExpanded !== tmsMatchDbId;
      if (opening) ensureDayAppointments(day);
      setAheadExpanded(opening ? tmsMatchDbId : null);
    },
    [aheadExpanded, ensureDayAppointments],
  );

  const pitch =
    [...new Set(todayFixtures.map((fixture) => fixture.pitch))].filter(
      (entry) => entry !== null,
    )[0] ?? null;

  const topAlert = alerts.find((alert) => !actioned.has(alert.id)) ?? null;

  const selectedFixtureCount =
    selectedDay === null
      ? 0
      : fixtures.filter(
          (fixture) =>
            fixture.date === selectedDay &&
            (competition === "all" || fixture.competitionGender === competition),
        ).length;
  const selectedPage =
    selectedDay === null || !aheadDays.includes(selectedDay)
      ? null
      : Math.floor(aheadDays.indexOf(selectedDay) / LOOKAHEAD_DAYS_PER_PAGE) + 1;

  const loading = schedule.status === "loading" || roster.status === "loading";
  const failure =
    schedule.status === "error"
      ? schedule.error
      : roster.status === "error"
        ? roster.error
        : reports.status === "error"
          ? reports.error
          : null;

  /* ── Draw ──────────────────────────────────────────────────────────────── */

  return (
    <>
      {/*
       * §4: the strip shows the single highest-urgency unresolved item and is **hidden
       * entirely** when nothing is outstanding — a permanently present strip stops being
       * read. An actioned alert (gap N4) is resolved, so the strip moves on to the next.
       */}
      {topAlert === null ? null : (
        <div className="alertstrip" role="status" data-testid="alertstrip">
          <span className="dot" aria-hidden="true" />
          <span className="alertstrip__label">Action required</span>
          <span className="alertstrip__msg">
            <strong>{topAlert.lead}</strong> {topAlert.rest}
          </span>
          {topAlert.href === null ? null : (
            <Link className="alertstrip__act" to={topAlert.href}>
              {topAlert.hrefLabel} →
            </Link>
          )}
        </div>
      )}

      {/*
       * §4's hero band.
       *
       * **The venue photograph.** It is still not copied into this repository — event artwork is
       * licensed and reaches a server operationally — but since run 32 the band draws whatever
       * `GET /brand/hero` serves, which is the file Tournament Configuration §01 put in the
       * store. Three layers, in this order: the duotone gradient on `.hero` itself, which is the
       * whole background when there is no photograph and stays underneath it when there is; the
       * photograph, `cover`; and `.hero__scrim`, which is the gradient overlay §4 describes and
       * is what keeps the kicker, the heading and the countdown legible over an image nobody in
       * this build has seen. Removing the photograph leaves exactly the band that was here
       * before, at exactly the same proportions.
       *
       * The slogan and its Japanese line are Configuration §01 `Slogan`, which the tournament
       * record carries as a nullable field; when it holds none, the band names the event rather
       * than printing a slogan nobody set.
       */}
      <section className="hero" aria-label="Tournament overview">
        {hero.asset === null ? null : (
          <div
            className="hero__photo"
            aria-hidden="true"
            data-testid="hero-photo"
            style={{ backgroundImage: `url("${hero.asset.url}")` }}
          />
        )}
        <div className="hero__scrim" aria-hidden="true" />
        <div className="hero__inner">
          <div>
            <span className="kicker">
              {tournament === null ? "Tournament" : tournament.hostCity}
              {openingDay === null || closingDay === null
                ? ""
                : ` · ${shortDate(openingDay)} – ${shortDate(closingDay)}`}
            </span>
            <h2>{tournament?.shortDisplayName ?? "TD Control Centre"}</h2>
            <p>
              {fixtures.length} matches · {officials.length} officials under TD oversight
            </p>
          </div>
          <div className="hero__count">
            {upcoming === null ? (
              <>
                <span className="kicker">No fixture remains</span>
                <span className="num">—</span>
                <span className="sub">Every match in the schedule has been played</span>
              </>
            ) : (
              <>
                <span className="kicker">
                  Next push-back · {upcoming.competitionGender === "m" ? "Men's" : "Women's"}{" "}
                  {upcoming.displayMatchNo}
                </span>
                <span className="num" data-testid="hero-count">
                  {countdown(upcoming, now) ?? "Under way"}
                </span>
                <span className="sub">
                  {upcoming.homeTeamCode ?? upcoming.homePlaceholder ?? "?"} v{" "}
                  {upcoming.awayTeamCode ?? upcoming.awayPlaceholder ?? "?"} · {upcoming.time}
                  {upcoming.pitch === null ? "" : ` · ${upcoming.pitch}`}
                </span>
              </>
            )}
          </div>
        </div>
      </section>

      {/* §5's day strip. One button per calendar day in the window, rest days included. */}
      {strip.length === 0 ? null : (
        <div className="daystrip-wrap">
          <nav
            className="daystrip"
            aria-label="Tournament days — click a day to jump to it in Tomorrow Onwards"
          >
            {strip.map((entry) => (
              <button
                key={entry.day}
                type="button"
                className={entry.isPast ? "past" : undefined}
                aria-pressed={selectedDay === entry.day}
                aria-label={entry.ariaLabel}
                onClick={() => {
                  setSelectedDay((current) => (current === entry.day ? null : entry.day));
                  const index = aheadDays.indexOf(entry.day);
                  if (index >= 0) setPage(Math.floor(index / LOOKAHEAD_DAYS_PER_PAGE) + 1);
                }}
              >
                <span className="dl">{entry.monthLabel}</span>
                <span className="dd">{entry.dayLabel}</span>
                <span className="dm" aria-hidden="true">
                  {entry.hasMen ? <i className="dm-m" /> : null}
                  {entry.hasWomen ? <i className="dm-w" /> : null}
                  {entry.hasHearing ? <i className="dm-dc" /> : null}
                  {!entry.hasMen && !entry.hasWomen && !entry.hasHearing ? <i /> : null}
                </span>
              </button>
            ))}
          </nav>
          <p className="daystrip__note" aria-live="polite" data-testid="day-note">
            {dayNote({
              selected: selectedDay,
              today,
              fixturesOnDay: selectedFixtureCount,
              pageOfLookahead: selectedPage,
              filtered: competition !== "all",
              windowFirst: openingDay ?? today,
              windowLast: closingDay ?? today,
            })}
          </p>
        </div>
      )}

      {/* §5's dateline. */}
      <div className="dateline">
        <h2>
          {focus.day === null ? "No Match Day" : longDate(focus.day)}{" "}
          <span className="md">
            {focus.index === null
              ? ""
              : `— Matchday ${String(focus.index)} of ${String(focus.total)}`}
            {focus.day !== null && !focus.isToday ? " · next match day" : ""}
          </span>
        </h2>
        <HowToUse id="command-desk" title="How to use the Command Desk">
          <li>
            The <HowToLink to="alertstrip">Action required</HowToLink> strip at the top carries
            the single most urgent thing outstanding, and disappears when nothing is. A strip
            that were always there would stop being read.
          </li>
          <li>
            <HowToLink to="today">Today&rsquo;s Matches</HowToLink> is the day in view. Press{" "}
            <b>Sheet</b> on any row to open its seven-slot appointment sheet; a name in the
            sheet opens that official&rsquo;s profile. Under each row&rsquo;s crew glance,{" "}
            <b>TO · SJ · TJ</b> carry a chip: <b>Draft</b> until the day is marked locked on TO
            Appointments, <b>On TMS</b> once it is, <b>Needs Re-Push</b> after an unlock.
          </li>
          <li>
            <HowToLink to="actual">Actual Appointments — Completed</HowToLink> counts, for each
            official, the locked TO, SJ and TJ appointments on matches already played. Drafts
            are never counted. A name opens the official&rsquo;s profile.
          </li>
          <li>
            The <HowToLink to="daystrip">day strip</HowToLink> runs the whole tournament
            window. Press a day to jump the lookahead list to it; press it again to clear.
          </li>
          <li>
            <HowToLink to="rail">The rail</HowToLink> answers &ldquo;what must I chase&rdquo;:
            how much of the day in view and the next match day is appointed, what is alerting,
            and how far the reports have got. Press <b>Done</b> on an alert to move it to{" "}
            <b>Actioned today</b> — it is not deleted.
          </li>
          <li>
            <HowToLink to="summaries">Tech Table Appointments</HowToLink> and{" "}
            <b>Umpires Appointments</b> show <b>completed / appointed</b> for every TO, Judge
            and Umpire across the <b>whole tournament</b> — not just the days on screen.
          </li>
        </HowToUse>
        <span className="spacer" />
        <span className="count">
          {focus.day === null ? (
            "No fixtures loaded"
          ) : (
            <>
              {/* RUN 82J (third QA, L11) — `1 Match`, through the appointment pages' pluraliser. */}
              Showing <b>{countOf(tally.total, "Match")}</b> · {tally.played} Played · {tally.live}{" "}
              Live · {tally.toCome} To Come
              {aheadDays.length === 0 ? "" : ` · Next match day ${shortDate(aheadDays[0] ?? "")}`}
            </>
          )}
        </span>
      </div>

      {/*
       * RUN 82J (third QA, L9) — the one place `Refresh Failing` is announced: visually hidden, a
       * `status`, and changed only when a stamp starts or stops failing (`useRefreshAnnouncement`).
       * It stands outside the grid so that it is on the page before the first change it announces.
       */}
      <p className="sr-only" role="status" data-testid="refresh-announcer">
        {refreshAnnouncement}
      </p>
      {/*
       * RUN 86 (QA M2) - the one place a failed TMS cards read is announced, beside the one above
       * and for its reason: on the page before the first change it announces, and changed only
       * when the set of failing competitions does (`useCardsAnnouncement`).
       */}
      <p className="sr-only" role="status" data-testid="cards-announcer">
        {cardsAnnounced}
      </p>

      {failure === null ? null : <FailBanner error={failure} />}

      {loading ? (
        <Skeleton rows={10} />
      ) : (
        /*
         * RUN 82J (third QA, L8) — a `div`, where it was a `main`. The Shell already draws the
         * page's one `main` around every route, and a `main` inside it was a second landmark of the
         * same kind, nested. The class, and so the grid, is unchanged.
         */
        <div className="maingrid">
          <div className="maincol" data-howto="today">
            <TodayPanel
              day={focus.day}
              fixtures={todayFixtures}
              appointed={appointed}
              expanded={expanded}
              onToggle={onToggle}
              pitch={pitch}
              openingDay={openingDay}
              now={minute}
              asOf={todayAsOf}
              crews={crewAlerts}
            />

            <LookaheadPanel
              days={lookahead}
              appointed={lookaheadAppointed}
              /* RUN 82T (H-1) — where each of its days' own reads stands, beside the index. */
              reads={lookaheadReads}
              expanded={aheadExpanded}
              onToggle={onToggleAhead}
              page={currentPage}
              pageCount={pageCount}
              daysPerPage={LOOKAHEAD_DAYS_PER_PAGE}
              onPage={setPage}
              highlightDay={selectedDay}
              now={minute}
              tabs={
                <Tabs
                  label="Competition filter"
                  options={COMPETITION_TABS}
                  value={competition}
                  onChange={(value) => {
                    setCompetition(value);
                    setPage(1);
                  }}
                  tint="competition"
                />
              }
            />

            {/*
             * RUN 82 (§8b) — `Actual Appointments — Completed`, in the place Cards Listing held.
             * The locked record of who officiated, tallied per official, from the schedule-wide
             * read §10 already makes; its loading and refusal are its own, as §10's are.
             */}
            <ActualAppointmentsPanel
              record={record}
              loading={scheduleAppointments.status === "loading"}
              failure={
                scheduleAppointments.status === "error" ? scheduleAppointments.error : null
              }
              asOf={recordAsOf}
            />
          </div>

          <aside className="rail" aria-label="Status and alerts" data-howto="rail">
            <AppointmentStatusPanel
              status={status}
              provisional={provisional}
              apptWindow={apptWindow}
            />
            <AlertsPanel
              alerts={alerts}
              actioned={actioned}
              onAction={onAction}
              crews={crewAlerts}
              crewDay={focus.day}
              crewDayIsToday={focus.isToday}
            />
            <InboxPanel />
            {/* RUN 82 (§8a) — §8's Cards Listing, moved here from the left column. */}
            <CardsListingPanel cards={cards} now={cardsNow} staleRead={cardsStale} />
            <UsefulLinksPanel />
            <OfficialsDonutPanel donut={donut} />
            <CardsAveragePanel cards={cards} now={cardsNow} staleRead={cardsStale} />
            <PoolProgressPanel progress={progress} />
            <ReportsOutstandingPanel outstanding={outstanding} total={reportTotal} />
            {/*
             * RULING R4″ (Alfred, 2026-09-09) — the ninth rail section, and not one of §9's
             * eight. It reads its own route (`GET /td/um-activity`) rather than being composed
             * from the five reads above, because it is the one figure on this desk that is not
             * derivable from another module's data. See `UmActivityPanel.tsx` on why it belongs
             * beside Appointment Status and Reports Outstanding: "is anybody actually writing
             * feedback?" is a what-must-I-chase question, and this is the rail that answers those.
             */}
            <UmActivityPanel />
            {/*
             * RUN 97 (the TD, 27 Sep 2026) — the Cap Milestones Watch: a few highlights and the
             * `Refresh & Generate` press, after the last section so nothing §9 orders moves. It
             * reads its own route, like UM Activity, and judges "played" on the schedule this page
             * already holds, by the Desk's one rule, on the Desk's minute.
             */}
            <CapMilestonesPanel fixtures={fixtures} now={minute} />
          </aside>
        </div>
      )}

      {/* §10's two full-width summaries. */}
      <section
        className="apptsum apptsum--desk"
        aria-label="Appointment summaries"
        data-howto="summaries"
      >
        <div className="apptsum__legend">
          <span className="kicker">Officials workload · completed / appointed</span>
          <span className="sw sw--m">
            <i aria-hidden="true" />
            Male Official
          </span>
          <span className="sw sw--f">
            <i aria-hidden="true" />
            Female Official
          </span>
          <span>Sorted By Appointed, Then Completed</span>
        </div>
        <TechTableSummary
          rows={workloadRows}
          role={techRole}
          onRole={(value) => {
            setTechRole(value);
            setTechPage(1);
          }}
          page={techPage}
          onPage={setTechPage}
          loading={scheduleAppointments.status === "loading"}
          failure={
            scheduleAppointments.status === "error" ? scheduleAppointments.error : null
          }
        />
        <UmpiresSummary
          rows={umpireRows}
          gender={umpGender}
          onGender={(value) => {
            setUmpGender(value);
            setUmpPage(1);
          }}
          page={umpPage}
          onPage={setUmpPage}
          loading={umpireSchedule.status === "loading"}
          failure={umpireSchedule.status === "error" ? umpireSchedule.error : null}
        />
      </section>

      {/*
       * §12's pitch-side capture companion. The mockup draws a phone with a roster select and
       * a dictation button; the view it is a picture OF is built, at `/um`, so this links to
       * the real thing rather than mocking it with a roster nobody appointed. §12's binding
       * decision B3/D22 — the dictation is the phone's own keyboard mic, on device, and no
       * audio file is ever created — is stated because it is the reason the view is safe to
       * use beside a pitch.
       */}
      <section className="companion" aria-labelledby="companionTitle">
        <div className="companion__copy">
          <span className="kicker kicker--accent">Bonus · Pitch-Side Capture View</span>
          <h3 id="companionTitle">Capture feedback between matches.</h3>
          <p>
            The companion view runs in any phone browser under the same sign-in. Three taps and
            a sentence — the note lands, timestamped, in the official&rsquo;s category log on
            the Reporting Workbench.
          </p>
          <ul>
            <li>
              <span className="n">01</span> Select the official from the tournament roster
            </li>
            <li>
              <span className="n">02</span> Choose a grading category — role-specific
            </li>
            <li>
              <span className="n">03</span> Type it, or use your keyboard&rsquo;s mic; the note
              files itself to the desktop workbench
            </li>
          </ul>
          <p style={{ marginTop: 18 }}>
            <Link className="linkbtn" to="/um">
              Open the capture view <span aria-hidden="true">→</span>
            </Link>
          </p>
        </div>
        <div className="companion__copy">
          <p>
            <b>Dictation is the phone&rsquo;s own keyboard mic, on the device.</b> No audio
            file is ever created or stored, and the AI service receives text only — never
            audio. The label is <i>Type Or Dictate</i> throughout, before and after a note is
            captured.
          </p>
          <p style={{ marginTop: 14 }}>
            Grading categories are role-specific and come from the same standard FIH per-role
            marking sets that drive the marking panel, so a captured note files straight into
            the criterion the TMS marking form uses.
          </p>
        </div>
      </section>

      {/*
       * §12's capture, built rather than drawn. The band above describes the companion and
       * links to the UM app's capture view; this is the TD's OWN half of it, which had no
       * surface at all before run 30 — his notes go to a separate store from the UM stream
       * (um-app/00-overview §2), so the UM capture view could never have written them.
       *
       * It is HERE and not on the Official Profile because spec 04 §B7 says so in terms: the
       * profile is where quick notes are read, never written, and that is to be preserved.
       */}
      <TdQuickNoteCapture />
    </>
  );
}
